// GENERATED by server/scripts/gen-grade.mts from server/src/grade.ts and the spikes in server/src/languages.ts — do not edit; run `npm run grade -w server`.
/**
 * The spelling grader (v1 ticket 05, spec "Spelling grader"): the one check
 * for every typed answer, in practice, review, exams and the starter check,
 * and later in loading and defence (tickets 19 and 20).
 *
 * It compares spike by spike. A spike is a small mark a native reader would
 * read past but that still carries meaning: an accent, a diaeresis, a
 * cedilla, an apostrophe, a hyphen, Catalan's l·l. Each language lists its
 * spikes (`LanguagePair.spikes` in languages.ts). An answer that differs from
 * an accepted one only in its spikes is a scratch: a pass, with each missed
 * spot named. Any other difference, a letter or a space, is wrong. Capitals
 * and punctuation that is not a spike (end punctuation, ¿ ¡, quotes, commas)
 * never count. A mark that is not a spike of the language is part of its
 * letter: Spanish ñ is a letter, not n with a spike.
 *
 * Persian keeps today's rules: Arabic kaf and yeh read as Persian, a
 * zero-width joiner or non-joiner reads as a space, short-vowel marks and the
 * tatweel are ignored, and it has no spikes, so apostrophes and hyphens in
 * Finglish are ignored as before. Its Finglish variants are accepted forms.
 * It also reads its joins loosely (`GradeOptions.looseJoins`): a word's parts
 * typed together, apart or with a half-space or hyphen are the same answer
 * (میخوام, می‌خوام, می خوام; mikham, mi kham, mi-kham), a keyboard habit
 * rather than a gap in the language. Only the gaps loosen: the letters still
 * have to match one for one, so a letter more, less or different is wrong.
 *
 * Single source. The server grades with this file; `npm run grade -w server`
 * (run by the root build and typecheck) copies it word for word to
 * `web/src/grade.generated.ts`, with each language's spikes appended. Keep it
 * free of imports and side effects so the copy compiles on both sides.
 */

/** Bump when the shared deterministic answer-grading rules change. */
export const GRADE_VERSION = 1;

/** The kinds of spike a language can count. */
export type Spike = 'accent' | 'diaeresis' | 'cedilla' | 'apostrophe' | 'hyphen' | 'middot' | 'ligature';

/** One missed spike, placed in the expected answer. */
export interface Miss {
  spike: Spike;
  /** Where the spot is in `expected` (a UTF-16 index) and how long it is; 0 is a point between two characters. */
  at: number;
  length: number;
  /** What the expected answer has there, and what was typed ('' for nothing). */
  want: string;
  got: string;
}

/** How a language reads an answer beyond its spikes. */
export interface GradeOptions {
  /**
   * A space, a half-space or a hyphen inside the answer never counts, so a word's parts may be typed together
   * or apart (Persian, which has no hyphen spike). The letters still compare one for one.
   */
  looseJoins?: boolean;
}

/** Right, a scratch (a pass with each missed spike listed, in order) or wrong. */
export type Verdict =
  | { kind: 'right'; expected: string }
  | { kind: 'scratched'; expected: string; missed: Miss[] }
  | { kind: 'wrong' };

/** Combining marks that can be spikes, by kind. Acute, grave and circumflex are all accents. */
const MARK_SPIKE: Readonly<Record<string, Spike>> = {
  '̀': 'accent', '́': 'accent', '̂': 'accent',
  '̈': 'diaeresis',
  '̧': 'cedilla',
};
/** Joiners inside a word, as they are compared. */
const JOINER_SPIKE: Readonly<Record<string, Spike>> = { "'": 'apostrophe', '-': 'hyphen', '·': 'middot', '⁀': 'ligature' };
/**
 * French œ and æ with the ligature spike: two letters (o e, a e) with a
 * ligature joiner between them, so "coeur" for "cœur" is a scratch.
 */
const LIGATURES: Readonly<Record<string, [string, string]>> = { 'œ': ['o', 'e'], 'Œ': ['o', 'e'], 'æ': ['a', 'e'], 'Æ': ['a', 'e'] };
/** Straight, curly and look-alike apostrophes. */
const APOSTROPHES = new Set(["'", '’', '‘', 'ʼ', '´', '`', '′']);
const HYPHENS = new Set(['-', '‐', '‑']);
/** What people type for the middle dot of l·l: the dot itself, a full stop, a bullet. */
const MIDDOTS = new Set(['·', '·', '.', '•', '∙', '⋅']);

interface Letter { base: string; marks: string; at: number; length: number }
interface Gap { space: boolean; joiners: string; at: number; length: number; joinAt: number; joinLength: number }
/** A text as the grader reads it: letters, and the gaps before, between and after them. */
interface Spelled { source: string; letters: Letter[]; gaps: Gap[] }

const isLatin = (ch: string) => /\p{Script=Latin}/u.test(ch);

/** Reads a text into letters and gaps under a language's spikes. */
function spell(text: string, spikes: readonly Spike[]): Spelled {
  const source = text.normalize('NFC');
  const letters: Letter[] = [];
  const gaps: Gap[] = [];
  const openGap = (at: number): Gap => ({ space: false, joiners: '', at, length: 0, joinAt: -1, joinLength: 0 });
  let gap = openGap(0);
  const join = (j: string, at: number, length: number) => {
    if (!gap.joiners.endsWith(j)) gap.joiners += j;
    if (gap.joinAt < 0) gap.joinAt = at;
    gap.joinLength = at + length - gap.joinAt;
  };
  const pushLetter = (l: Letter) => {
    gap.length = l.at - gap.at;
    gaps.push(gap);
    letters.push(l);
    gap = openGap(l.at + l.length);
  };
  for (let i = 0; i < source.length;) {
    const ch = String.fromCodePoint(source.codePointAt(i)!);
    const n = ch.length;
    const prev = source[i - 1] ?? '';
    const next = source[i + n] ?? '';
    if (/\s/u.test(ch) || ch === '‌' || ch === '‍') gap.space = true;
    else if (APOSTROPHES.has(ch)) { if (spikes.includes('apostrophe')) join("'", i, n); }
    else if (HYPHENS.has(ch)) { if (spikes.includes('hyphen')) join('-', i, n); }
    else if (MIDDOTS.has(ch)) { if (spikes.includes('middot') && /l/i.test(prev) && /l/i.test(next)) join('·', i, n); }
    else if (ch === 'ŀ' || ch === 'Ŀ') {
      // ŀ, the one-character l with a middle dot: an l, then the dot.
      pushLetter({ base: 'l', marks: '', at: i, length: n });
      if (spikes.includes('middot')) join('·', i, n);
    } else if (LIGATURES[ch] && spikes.includes('ligature')) {
      // Both letters sit on the ligature's one character; the joiner between them is the spike.
      const [a, b] = LIGATURES[ch];
      pushLetter({ base: a, marks: '', at: i, length: 0 });
      join('⁀', i, n);
      pushLetter({ base: b, marks: '', at: i, length: n });
    } else if (/[\p{L}\p{N}\p{S}]/u.test(ch)) {
      const folded = ch === 'ك' ? 'ک' : ch === 'ي' ? 'ی' : ch;
      if (isLatin(folded)) {
        const [base, ...marks] = [...folded.toLowerCase().normalize('NFD')];
        let letter = base;
        const spiky: string[] = [];
        for (const m of marks) {
          const kind = MARK_SPIKE[m];
          if (kind && spikes.includes(kind)) spiky.push(m);
          else letter += m;
        }
        pushLetter({ base: letter, marks: spiky.sort().join(''), at: i, length: n });
      } else if (folded !== 'ـ') {
        pushLetter({ base: folded.toLowerCase(), marks: '', at: i, length: n });
      }
    } else if (/\p{M}/u.test(ch) && !/[ً-ٟ]/.test(ch)) {
      // A combining mark NFC could not fold into its letter.
      const last = letters[letters.length - 1];
      if (last && gap.at === i && isLatin(last.base[0] ?? '')) {
        const kind = MARK_SPIKE[ch];
        if (kind && spikes.includes(kind)) last.marks = [...last.marks, ch].sort().join('');
        else last.base += ch;
        last.length += n;
        gap.at = i + n;
      }
    }
    // Anything else (punctuation that is not a spike, Persian short vowels, the tatweel) is ignored.
    i += n;
  }
  gap.length = source.length - gap.at;
  gaps.push(gap);
  return { source, letters, gaps };
}

/** The first character in one string and not the other. */
function firstDiff(a: string, b: string): string {
  for (const ch of a) if (!b.includes(ch)) return ch;
  for (const ch of b) if (!a.includes(ch)) return ch;
  return a[0] ?? b[0] ?? '';
}

/**
 * The spikes `typed` misses against `want`, in the order they sit in the
 * answer, or null when they differ in more than spikes (a letter, a letter
 * too many or too few, a space inside the answer unless `looseJoins`).
 */
function compare(typed: Spelled, want: Spelled, looseJoins = false): Miss[] | null {
  const n = want.letters.length;
  if (!n || typed.letters.length !== n) return null;
  const missed: Miss[] = [];
  for (let k = 0; k <= n; k++) {
    const tg = typed.gaps[k];
    const wg = want.gaps[k];
    const end = k === 0 || k === n;
    if (tg.joiners === wg.joiners) {
      // Spaces around a shared joiner never count ("d' acord"); a space inside
      // the answer does ("de nada" is not "denada"), unless joins are loose
      // ("mi kham" is "mikham"), where only the letters around it count.
      if (!end && !wg.joiners && tg.space !== wg.space && !looseJoins) return null;
    } else {
      const spike = JOINER_SPIKE[firstDiff(wg.joiners, tg.joiners)];
      // A ligature is one word: "co eur" is not "cœur".
      if (spike === 'ligature' && tg.space !== wg.space) return null;
      // A missed ligature was typed as its two letters ("oe" for "œ").
      const got = spike === 'ligature' && !end && tg.joinAt < 0
        ? typed.source.slice(typed.letters[k - 1].at, typed.letters[k].at + typed.letters[k].length)
        : tg.joinAt >= 0 ? typed.source.slice(tg.joinAt, tg.joinAt + tg.joinLength) : '';
      if (wg.joinAt >= 0) {
        missed.push({ spike, at: wg.joinAt, length: wg.joinLength, want: want.source.slice(wg.joinAt, wg.joinAt + wg.joinLength), got });
      } else {
        // A joiner typed where the answer has none: the spot is the gap itself.
        const length = end ? 0 : wg.length;
        const at = k === 0 ? want.letters[0].at : wg.at;
        missed.push({ spike, at, length, want: want.source.slice(at, at + length), got });
      }
    }
    if (k === n) break;
    const tl = typed.letters[k];
    const wl = want.letters[k];
    if (tl.base !== wl.base) return null;
    if (tl.marks !== wl.marks) {
      missed.push({
        spike: MARK_SPIKE[firstDiff(wl.marks, tl.marks)],
        at: wl.at,
        length: wl.length,
        want: want.source.slice(wl.at, wl.at + wl.length),
        got: typed.source.slice(tl.at, tl.at + tl.length),
      });
    }
  }
  return missed;
}

/** How many spikes a text carries under a language's list. */
function spikeCount(s: Spelled): number {
  return s.letters.reduce((n, l) => n + l.marks.length, 0) + s.gaps.reduce((n, g) => n + g.joiners.length, 0);
}

/**
 * Grades a typed answer against its accepted forms under a language's spikes
 * (and its options: `looseJoins` for Persian).
 * The best reading wins: right, else the scratch with the fewest misses, else
 * wrong. `expected` is the accepted form it was read against (NFC), so each
 * miss's position indexes it.
 *
 * An accepted form that is another accepted form with spikes missing (the
 * accent-less copies older accept lists carry, "gracies" next to "gràcies")
 * is not a form of its own: typing it is a scratch against the full one.
 */
export function grade(typed: string, accepted: readonly unknown[], spikes: readonly Spike[], options: GradeOptions = {}): Verdict {
  const loose = options.looseJoins === true;
  const t = spell(String(typed ?? ''), spikes);
  if (!t.letters.length) return { kind: 'wrong' };
  const forms: Spelled[] = [];
  for (const a of accepted) {
    if (typeof a !== 'string') continue;
    const s = spell(a.trim(), spikes);
    if (s.letters.length && !forms.some((f) => f.source === s.source)) forms.push(s);
  }
  const own = forms.filter((a) => !forms.some((b) => b !== a && spikeCount(b) > spikeCount(a) && (compare(a, b, loose)?.length ?? 0) > 0));
  let best: Verdict = { kind: 'wrong' };
  for (const f of own) {
    const missed = compare(t, f, loose);
    if (missed === null) continue;
    if (!missed.length) return { kind: 'right', expected: f.source };
    if (best.kind === 'wrong' || missed.length < best.missed.length) best = { kind: 'scratched', expected: f.source, missed };
  }
  return best;
}

/** A pass for the box: right, or a scratch. */
export function passes(v: Verdict): boolean {
  return v.kind !== 'wrong';
}

/** Each target language's spikes (`LanguagePair.spikes` in server/src/languages.ts). */
export const SPIKES_BY_TARGET: Readonly<Record<string, readonly Spike[]>> = {
  "ca": ["accent","diaeresis","cedilla","apostrophe","hyphen","middot"],
  "fr": ["accent","diaeresis","cedilla","apostrophe","hyphen","ligature"],
  "fa": [],
  "en": ["apostrophe","hyphen"],
  "es": ["accent","diaeresis"],
};

/** The spikes for a target; an unknown one has none (today's rules). */
export function spikesFor(target: string | undefined): readonly Spike[] {
  return (target !== undefined && SPIKES_BY_TARGET[target]) || [];
}

/** The targets whose joins the grader reads loosely (`LanguagePair.looseJoins` in server/src/languages.ts). */
export const LOOSE_JOINS: readonly string[] = ["fa"];

/** The grader's options for a target; an unknown one has none. */
export function gradeOptionsFor(target: string | undefined): GradeOptions {
  return target !== undefined && LOOSE_JOINS.includes(target) ? { looseJoins: true } : {};
}
